/**
 *
 * @class LeafletLoadJob
 *
 * The work of loading the clusters is broken up into something we can run incrementally,
 * and do in small pieces...so we can have progress shown to the user and not completely
 * block the GUI while we do the long-running task.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      July 2022
 *
 */
import {LeafletViewer} from "@/components/leaflet/leaflet.viewer";
import {Feature, FeatureCollection, Geometry} from "geojson";
import {DistinctColorPalette} from "@/helpers/distinct.color.palette";
import {Logger} from "@/util/logger";
import * as L from "leaflet";
import {LatLngBounds, Layer, LeafletEvent, LeafletMouseEvent, Marker} from "leaflet";
import * as $ from "jquery";
import {first} from "rxjs/operators";
import {Panorama} from "@/models/panorama";
import {errorAsText} from "@/util/util";
import {ParamMap} from "@angular/router";

export class LeafletLoadJob {

  /** the viewer the job is for */
  private _viewer: LeafletViewer         = null;

  /** what general part of the work are we doing? */
  private _state: string                 = '';

  /** which small piece of the general section are we working on? (our resume index) */
  private _increment: number             = 0;

  /** the Features to use with GeoJSON */
  private _collection: FeatureCollection = null;

  /** the icon we use for enabled map markers */
  private _markerIcon: L.Icon            = null;

  /** the icon we use for disabled map markers */
  private _disabledMarkerIcon: L.Icon    = null;

  constructor(viewer: LeafletViewer) {

    this._viewer = viewer;
  }

  isDone(): boolean {

    return this._state == 'done';
  }

  /** do an increment of work */
  doWork() {

    let job: LeafletLoadJob = this;

    if (job.isDone()) {

      /* nothing more to do */

      return ;
    }

    if (job._state == '') {

      /* kick off the job ... */

      Logger.debug(`[LeafletLoadJob] entering 'start' state`);

      /*
       * build up the GeoJSON feature collection, so we can then add that
       * as a layer to leaflet.
       *
       */

      job._viewer.setStatus('Building corridors...');

      job._viewer.setupRouteGeometry();

      Logger.debug(`[LeafletLoadJob] transition to 'routes' state`);

      job._state = 'routes';

      requestAnimationFrame(() => {job.doWork()});

      return;
    }

    if (job._state == 'routes') {

      /* kick off the job ... */

      Logger.debug(`[LeafletLoadJob] entering 'start' state`);

      Logger.debug('[LeafletLoadJob] routes layer: ', job._collection);

      job._viewer.setupRouteLayer();

      /* loading the line strings is the first 10% of our load progress.  Remainder is the clustering */

      job._viewer.setStatus('Corridors created.');

      job._viewer.setProgress(10.0);

      Logger.debug(`[LeafletLoadJob] transition to 'clusters' state`);

      job._state = 'clusters';

      requestAnimationFrame(() => {job.doWork()});

      return;
    }

    if (job._state == 'clusters') {

      Logger.debug(`[LeafletLoadJob] entering 'clusters' state`);

      /* add layer for the clusters markers */

      Logger.debug('[LeafletViewer] making marker clusters...');

      if (!job._viewer.clusters()) {

        /* first time through on incremental cluster creation */

        job._viewer.setClusters(L.markerClusterGroup({
          maxClusterRadius: (zoomLevel: number): number => {

            let radius = 80;

            if (zoomLevel > 12) {

              radius = 40;
            }

            return radius;
          }

        }));

        job._markerIcon =
          L.icon({
            iconSize: [25, 41],
            iconAnchor: [10, 41],
            popupAnchor: [2, -40],
            iconUrl: "/assets/images/marker-enabled.png",
            shadowUrl: "https://unpkg.com/leaflet@1.4.0/dist/images/marker-shadow.png"
          })

        job._disabledMarkerIcon =
          L.icon({
            iconSize: [25, 41],
            iconAnchor: [10, 41],
            popupAnchor: [2, -40],
            iconUrl: "/assets/images/marker-disabled.png",
            shadowUrl: "https://unpkg.com/leaflet@1.4.0/dist/images/marker-shadow.png"
          })
      }

      let max:   number = 1000;
      let start: number = job._increment;
      let end:   number = start + max;

      if (end >= job._viewer.points().length) {

        end = job._viewer.points().length - 1;
      }

      job._viewer.setStatus(`Clustering panoramas ${start} - ${end}...`);

      /* do SOME of the work ... */

      for (let i = start; i <= end; i++) {

        let item: any = job._viewer.points()[i];

        const lat = item.y;
        const lng = item.x;

        /* set the marker style */

        let marker = L.marker(
          new L.LatLng(lat, lng), {
            icon: job._markerIcon,
          });

        if (!item.enabled) {

          marker = L.marker(
            new L.LatLng(lat, lng), {
              icon: job._disabledMarkerIcon,
            });
        }

        marker['data'] = item;

        marker.on('contextmenu', (evt: LeafletMouseEvent) => {

          let m: Marker     = evt.target;
          let e: MouseEvent = evt.originalEvent;


          job._viewer.showMarkerContextMenu(m, e);

        });

        marker.on('mouseover', (evt: LeafletMouseEvent) => {

          let m: Marker     = evt.target;

          job._viewer.popMarkerHelp(m, evt.originalEvent);

        });

        marker.on('mouseout', (evt: LeafletMouseEvent) => {

          job._viewer.hideBubbleHelp();

        });

        marker.on('dblclick', () => {

          Logger.debug(`[LeafletViewer] jumping to panorama via marker dblclick: `, marker);

          let x: number = marker.getLatLng().lat;
          let y: number = marker.getLatLng().lng;
          let id: number = marker['data'].id;

          job._viewer.navigateTo(id);
        });

        job._viewer.clusters().addLayer(marker);

        job._increment = i;


      } /* end of partial progress on the clustering */

      job._viewer.setProgress(10 + Number(((job._increment / job._viewer.points().length) * 90.0).toFixed(2)));

      if (job._increment < (job._viewer.points().length - 1)) {

        /* we completed a chunk, yield back to the main application */

        job._state = 'clusters';

        Logger.debug(`[LeafletLoadJob] yielding...`);

        requestAnimationFrame(() => {job.doWork()});

        return ;
      }

      /* we're done clustering! */

      job._viewer.setStatus('Clustering completed.');

      job._viewer.clusters().on('clustercontextmenu', function (e: LeafletEvent) {

        let hull: { lat: number, lng: number }[] = e.sourceTarget.getConvexHull();
        let markers: Marker[] = e.sourceTarget.getAllChildMarkers();
        let found: Marker = null;

        if (hull.length >= 3) {

          /*
           * to know what marker we want to transition through to a panorama on, we want
           * the marker closest to the center...so first we need the center of the group
           * of points.
           *
           *   ref: https://stackoverflow.com/questions/34059116/what-is-the-fastest-way-to-find-the-center-of-an-irregular-convex-polygon
           *        https://stackoverflow.com/questions/20916953/get-distance-between-two-points-in-canvas
           *
           * General idea is to use the length of the line segments among the points to
           * weight the center, so that it's closer to the center of the shape of the bounding
           * polygon (should be line or curved line).  This also matters because we may have
           * more than one set of points in play (multiple captures of the same area), so
           * the boundary of *that* isn't just a line.
           */

          let sumCenterX: number = 0.0;
          let sumCenterY: number = 0.0;
          let sumWeight: number = 0.0;

          for (let i = 1; i <= (hull.length - 2); i++) {

            let p1x: number = hull[i - 1].lat;
            let p1y: number = hull[i - 1].lng;
            let p2x: number = hull[i].lat;
            let p2y: number = hull[i].lng;
            let p3x: number = hull[i + 1].lat;
            let p3y: number = hull[i + 1].lng;

            let length1: number = 0.0;
            let length2: number = 0.0;

            {
              let a = p2x - p3x;
              let b = p2y - p3y;

              length1 = Math.sqrt(a * a + b * b);
            }

            {
              let a = p2x - p1x;
              let b = p2y - p1y;

              length2 = Math.sqrt(a * a + b * b);
            }

            let weight = length1 + length2;

            sumWeight += weight;

            sumCenterX += p2x * weight;
            sumCenterY += p2y * weight;
          }

          sumCenterX /= sumWeight;
          sumCenterY /= sumWeight;

          /*
           * now with the center point in hand, we just need to know which marker
           * is closest to that point.
           *
           */

          let least: number = 9999999.0;


          for (let j = 0; j < markers.length; j++) {

            let p1x: number = markers[j].getLatLng().lat;
            let p1y: number = markers[j].getLatLng().lng;

            let a = p1x - sumCenterX;
            let b = p1y - sumCenterY;

            let distance = Math.sqrt(a * a + b * b);

            if (distance < least) {

              /* closer! */

              found = markers[j];

              least = distance;

              if (distance <= 0.00001) {

                /* likely within 5-10m...close enough! */

                break;
              }

            }

          }

        } else {

          /* just pick a marker */

          found = markers[0];
        }

        if (!found) {

          return;
        }

        /* launch the panorama view as usual on that marker ... */

        Logger.debug(`[LeafletViewer] jumping to panorama via cluster center: `, found);

        let x: number = found.getLatLng().lat;
        let y: number = found.getLatLng().lng;
        let id: number = found['data'].id;

        job._viewer.navigateTo(id);

      });

      job._viewer.clusters().addTo(job._viewer.map());

      let returnPosition: boolean = false;
      let rx: number = 0.0;
      let ry: number = 0.0;
      let rz: number = 0.0;

      /* do we have parameters in the URL that can tell us where to focus? */

      const params: ParamMap = job._viewer.activeRoute().snapshot['queryParamMap'];

      Logger.debug(`[LeafletViewer] map focus parameters`, params);

      if (params.has('rx') && params.has('ry') && params.has('rz')) {

        returnPosition = true;

        rx = Number(params.get('rx'));
        ry = Number(params.get('ry'));
        rz = Number(params.get('rz'));
      }

      /* find the bounding box of the routes */

      let bbox: LatLngBounds = job._viewer.routeLayer().getBounds();
      bbox.pad(0.02);

      Logger.debug('[LeafletViewer] map centered on routes.');

      job._viewer.setStatus('Centering map...');

      /* center the map as needed ... */

      if (returnPosition) {

        /* center the map at the position they are returning to */

        job._viewer.map().setView(L.latLng(rx, ry), rz);

      } else {

        /* (default) now pan/zoom so all the routes are visible ... */

        job._viewer.map().fitBounds(bbox);
      }

      job._viewer.setProgress(100.0);

      job._viewer.setStatus('Loaded!');

      /* we can get rid of the progress bar */

      let el: JQuery<HTMLElement> = $(`.load-progress-thin-bar`);
      $(el).css('width', `0px`);

      job._viewer.finalizeRender();

      Logger.debug(`[LeafletLoadJob] transition to 'done' state`);

      job._state = 'done';

      return;
    }

    /* job must be done */

    job._state = 'done';

    return;
  }

}
